import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Info } from 'lucide-react';
import { jobApplications, statuses, users, libraries, jobs } from '../services/apiService';
import { TALENT_TYPES } from '../utils/talentTypeUtils';
import { Loader } from '../components/UI/LoadingSpinner';
import ContactForm from '../components/UI/ContactForm';
import LinkForm from '../components/UI/LinkForm';
import { TextField, DatePicker, Autocomplete } from '@anyemp/ui-kit';
import CommunicationsManager from '../components/UI/CommunicationsManager';
import StatusTransitionModal from '../components/StatusManagement/StatusTransitionModal';
import toast from 'react-hot-toast';
import ResumeParserDrawer from '../components/UI/ResumeParserDrawer';
import ParsedDataPanel from '../components/UI/ParsedDataPanel';
import ConfirmDialog from '../components/UI/ConfirmDialog';
import ImportantDatesManager from '../components/UI/ImportantDatesManager';
import NotesManager from '../components/UI/NotesManager';
import { useAuth } from '../hooks/useAuth';
import { mapResumeDataToForm } from '../utils/resumeDataMapper';
import useFormPersistence from '../hooks/useFormPersistence';
import { formatApiError } from '../utils/formatApiError';
import FormActions from '../components/UI/FormActions';

/**
 * Job Application Form Component
 *
 * Features:
 * - Create/Edit job applications
 * - Auto-save form data to localStorage (only in create mode)
 * - AI Resume Parser integration
 * - Duplicate contact validation
 * - Communications management
 *
 * Auto-save behavior:
 * - Automatically saves form data to localStorage with 1.5s debounce
 * - Restores data on page reload/return
 * - Clears localStorage on:
 *   1. Successful job application creation
 *   2. User confirms "Discard Changes" when canceling
 * - Does NOT clear on:
 *   1. Accidental page navigation
 *   2. Browser refresh
 *   3. User clicks "Keep Editing" in cancel dialog
 * - Auto-save is disabled when editing existing job application
 *
 * Cancel behavior:
 * - Always shows confirmation dialog when creating (not editing)
 * - Dialog warns that unsaved data will be lost if user leaves
 * - No validation of whether data was actually entered
 */
const JobApplicationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [duplicateContacts, setDuplicateContacts] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [showAllCities, setShowAllCities] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [toolsOptions, setToolsOptions] = useState([]);
  const [linkToolsOptions, setLinkToolsOptions] = useState([]);
  const [professionsOptions, setProfessionsOptions] = useState([]);
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [jobPostOptions, setJobPostOptions] = useState([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [links, setLinks] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [isResumeParserOpen, setIsResumeParserOpen] = useState(false);

  // Состояние для AI Parsed Data Panel
  const [aiRawData, setAiRawData] = useState(null);
  const [unmappedData, setUnmappedData] = useState(null);
  const [isParsedDataPanelOpen, setIsParsedDataPanelOpen] = useState(false);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [dataRestoredFromStorage, setDataRestoredFromStorage] = useState(false);
  const restoredFromStorageRef = useRef(false);

  // Status transition modal state
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [transitionData, setTransitionData] = useState(null);
  const [pendingSubmitData, setPendingSubmitData] = useState(null); // Данные, ожидающие отправки после смены статуса
  const [originalStatusId, setOriginalStatusId] = useState(null); // Исходный статус при загрузке

  // Use ref instead of state - doesn't need to trigger re-renders
  const isFormInitialized = useRef(false);
  const [importantDates, setImportantDates] = useState([]);
  const [notes, setNotes] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    country_id: '',
    city_id: '',
    manager_id: user?.id ? String(user.id) : '',
    status_id: '',
    birthday: '',
    age: '',
    job_post_id: ''
  });

  // Form persistence - autosave only when creating (not editing)
  const STORAGE_KEY = 'jobApplicationFormDraft';
  const { saveToStorage, loadFromStorage, clearStorage } =
    useFormPersistence(STORAGE_KEY, !isEditing);

  const loadInitialData = async () => {
    try {
      setInitialLoading(true);

      await Promise.all([
        fetchStatusOptions(),
        fetchUserOptions(),
        fetchCountries(),
        fetchAllCities(),
        fetchTools(),
        fetchLinkTools(),
        fetchProfessions(),
        fetchJobPosts()
      ]);

      // Загружаем данные заявки при редактировании
      if (isEditing) {
        await fetchApplication();
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load initial data');
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [id]);

  // Update manager_id when user is loaded
  // Don't overwrite if data was restored from localStorage
  useEffect(() => {
    if (user?.id && !isEditing && !dataRestoredFromStorage) {
      setFormData(prev => ({
        ...prev,
        manager_id: String(user.id)
      }));
    }
  }, [user, isEditing, dataRestoredFromStorage]);

  // Automatically set first status when creating new job application
  // Don't overwrite if data was restored from localStorage
  useEffect(() => {
    if (!isEditing && statusOptions.length > 0 && !formData.status_id && !dataRestoredFromStorage) {
      setFormData(prev => ({
        ...prev,
        status_id: String(statusOptions[0].id)
      }));
    }
  }, [statusOptions, isEditing, formData.status_id, dataRestoredFromStorage]);

  // Load saved form data from localStorage (only when creating, not editing)
  useEffect(() => {
    if (!initialLoading && !isFormInitialized.current) {
      if (!isEditing) {
        // Try to restore data from localStorage
        const savedData = loadFromStorage();
        if (savedData) {
          restoredFromStorageRef.current = true;
          // Set flag to prevent auto-fill from overwriting restored data
          setDataRestoredFromStorage(true);

          // Restore formData
          if (savedData.formData) {
            setFormData(prev => ({
              ...prev,
              ...savedData.formData,
              // Keep manager_id from user if it was set
              manager_id: savedData.formData.manager_id || prev.manager_id,
              // Format birthday date for HTML date input (yyyy-MM-dd)
              birthday: savedData.formData.birthday ? savedData.formData.birthday.split('T')[0] : ''
            }));
          }

          // Restore contacts
          if (savedData.contacts && savedData.contacts.length > 0) {
            setContacts(savedData.contacts);
          }

          // Restore links
          if (savedData.links && savedData.links.length > 0) {
            setLinks(savedData.links);
          }

          // Restore selectedProfessions
          if (savedData.selectedProfessions && savedData.selectedProfessions.length > 0) {
            setSelectedProfessions(savedData.selectedProfessions);
          }

          // Restore communications
          if (savedData.communications && savedData.communications.length > 0) {
            setCommunications(savedData.communications);
          }

          // Restore important dates
          if (savedData.importantDates && savedData.importantDates.length > 0) {
            setImportantDates(savedData.importantDates);
          }

          // Restore notes
          if (savedData.notes && savedData.notes.length > 0) {
            setNotes(savedData.notes);
          }

          // If country was saved, fetch cities for that country
          if (savedData.formData?.country_id) {
            fetchCities(savedData.formData.country_id);
          }
        }
      }

      // Mark form as initialized (data either restored or no data to restore)
      isFormInitialized.current = true;
    }
  }, [initialLoading, isEditing, loadFromStorage]);

  // Auto-initialize 3 contact cards (Phone, Email, Telegram) when creating new job application
  useEffect(() => {
    // Wait until initial loading is complete and form is initialized
    if (!initialLoading && !isEditing && toolsOptions.length > 0 && contacts.length === 0 && !dataRestoredFromStorage) {
      // If there is a saved draft with contacts, do not overwrite it with defaults.
      // This protects against mount timing where restore effect hasn't applied state yet.
      const savedData = loadFromStorage();
      if (savedData?.contacts && savedData.contacts.length > 0) {
        restoredFromStorageRef.current = true;
        return;
      }

      if (restoredFromStorageRef.current) return;

      const phoneType = toolsOptions.find(tool => tool.name.toLowerCase() === 'phone');
      const emailType = toolsOptions.find(tool => tool.name.toLowerCase() === 'email');
      const telegramType = toolsOptions.find(tool => tool.name.toLowerCase() === 'telegram');

      const initialContacts = [];

      // Add Phone contact (primary by default)
      if (phoneType) {
        initialContacts.push({
          tool_id: phoneType.id,
          value: '',
          is_primary: true,
          is_verified: false
        });
      }

      // Add Email contact
      if (emailType) {
        initialContacts.push({
          tool_id: emailType.id,
          value: '',
          is_primary: false,
          is_verified: false
        });
      }

      // Add Telegram contact
      if (telegramType) {
        initialContacts.push({
          tool_id: telegramType.id,
          value: '',
          is_primary: false,
          is_verified: false
        });
      }

      // Only set contacts if we found at least one tool
      if (initialContacts.length > 0) {
        setContacts(initialContacts);
      }
    }
  }, [initialLoading, isEditing, toolsOptions, contacts.length, dataRestoredFromStorage, loadFromStorage]);

      // Auto-save form data to localStorage (with debounce)
      // Only save after form is initialized to avoid overwriting stored data during mount
      useEffect(() => {
        if (!isEditing && isFormInitialized.current) {
          saveToStorage({
            formData,
            contacts,
            links,
            selectedProfessions,
            communications,
            importantDates,
            notes
          });
        }
      }, [formData, contacts, links, selectedProfessions, communications, importantDates, notes, isEditing, saveToStorage]);

  const fetchStatusOptions = async () => {
    try {
      const response = await statuses.getStatusesByType(TALENT_TYPES.JOB_APPLICATION, { isCommon: 1 });
      if (response.success) {
        setStatusOptions(response.data);
      }
    } catch (err) {
      console.error('Error fetching statuses:', err);
      // Не выбрасываем ошибку, чтобы другие данные продолжили загружаться
    }
  };

  const fetchUserOptions = async () => {
    try {
      const response = await users.getAllUsers();
      if (response.success) {
        setUserOptions(response.data.users);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      // Не выбрасываем ошибку, чтобы другие данные продолжили загружаться
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await libraries.getCountries();

      // Обрабатываем разные форматы ответа
      let countries = [];
      if (response.success && response.data) {
        countries = response.data;
      } else if (Array.isArray(response)) {
        countries = response;
      } else if (response.data && Array.isArray(response.data)) {
        countries = response.data;
      }

      setCountryOptions(countries);
    } catch (err) {
      console.error('Error fetching countries:', err);
      // Не выбрасываем ошибку, чтобы другие данные продолжили загружаться
    }
  };

  const fetchCities = async (countryId) => {
    if (!countryId) {
      setCityOptions(allCities); // Показываем все города если страна не выбрана
      setShowAllCities(true);
      return;
    }

    try {
      setLoadingCities(true);
      const response = await libraries.getCitiesByCountry(countryId);
      
      // Обрабатываем разные форматы ответа
      let cities = [];
      if (response.success && response.data) {
        cities = response.data;
      } else if (Array.isArray(response)) {
        cities = response;
      } else if (response.data && Array.isArray(response.data)) {
        cities = response.data;
      }
      
      setCityOptions(cities);
      setShowAllCities(false); // Показываем только города выбранной страны
    } catch (err) {
      console.error('Error fetching cities:', err);
      setCityOptions(allCities); // В случае ошибки показываем все города
      setShowAllCities(true);
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchAllCities = async () => {
    try {
      const response = await libraries.getAllCities();

      // Обрабатываем разные форматы ответа
      let cities = [];
      if (response.success && response.data) {
        cities = response.data;
      } else if (Array.isArray(response)) {
        cities = response;
      } else if (response.data && Array.isArray(response.data)) {
        cities = response.data;
      }

      setAllCities(cities);
      setCityOptions(cities); // Изначально показываем все города
      setShowAllCities(true);
    } catch (err) {
      console.error('Error fetching all cities:', err);
      // Не выбрасываем ошибку, чтобы другие данные продолжили загружаться
    }
  };

  const fetchTools = async () => {
    try {
      const response = await libraries.getContactTools();

      // Обрабатываем разные форматы ответа
      let tools = [];
      if (response.success && response.data) {
        tools = response.data;
      } else if (Array.isArray(response)) {
        tools = response;
      } else if (response.data && Array.isArray(response.data)) {
        tools = response.data;
      }

      setToolsOptions(tools);
    } catch (err) {
      console.error('Error fetching tools:', err);
    }
  };

  const fetchLinkTools = async () => {
    try {
      const response = await libraries.getLinkTools();

      // Обрабатываем разные форматы ответа
      let tools = [];
      if (response.success && response.data) {
        tools = response.data;
      } else if (Array.isArray(response)) {
        tools = response;
      } else if (response.data && Array.isArray(response.data)) {
        tools = response.data;
      }

      setLinkToolsOptions(tools);
    } catch (err) {
      console.error('Error fetching link tools:', err);
    }
  };

  const fetchJobPosts = async () => {
    try {
      const response = await jobs.listPosts();
      let items = [];
      const payload = response?.data;
      if (Array.isArray(payload)) {
        items = payload;
      } else if (Array.isArray(payload?.data)) {
        items = payload.data;
      } else if (Array.isArray(payload?.rows)) {
        items = payload.rows;
      } else if (Array.isArray(payload?.items)) {
        items = payload.items;
      }
      setJobPostOptions(items);
    } catch (err) {
      console.error('Error fetching job posts:', err);
    }
  };

  const fetchProfessions = async () => {
    try {
      const response = await libraries.getProfessions();
      
      // Обрабатываем разные форматы ответа
      let professions = [];
      if (response.success && response.data) {
        professions = response.data;
      } else if (Array.isArray(response)) {
        professions = response;
      } else if (response.data && Array.isArray(response.data)) {
        professions = response.data;
      }
      
      setProfessionsOptions(professions);
    } catch (err) {
      console.error('Error fetching professions:', err);
    }
  };

  const fetchApplication = async () => {
    try {
      const response = await jobApplications.getJobApplicationById(id);

      if (response.success) {
        const app = response.data;
        setFormData({
          name: app.name || '',
          gender: app.gender || '',
          country_id: app.country_id ? String(app.country_id) : '',
          city_id: app.city_id ? String(app.city_id) : '',
          manager_id: app.manager_id ? String(app.manager_id) : '',
          status_id: app.status_id ? String(app.status_id) : '',
          birthday: app.birthday ? app.birthday.split('T')[0] : '',
          age: app.age || '',
          job_post_id: app.job_post_id ? String(app.job_post_id) : ''
        });

        // Если выбранный job_post не попал в первую страницу — дозагрузим его отдельно
        if (app.job_post_id) {
          try {
            const postResponse = await jobs.getPostById(app.job_post_id);
            const post = postResponse?.data?.data || postResponse?.data;
            if (post && post.id) {
              setJobPostOptions(prev => {
                if (prev.some(p => p.id === post.id)) return prev;
                return [post, ...prev];
              });
            }
          } catch (err) {
            // Пост мог быть удалён в jobs-service — оставляем job_post_id, UI покажет "[Deleted Post]"
            console.warn(`Job post ${app.job_post_id} not found in jobs-service:`, err.message);
          }
        }

        // Сохраняем исходный статус для проверки изменений
        setOriginalStatusId(app.status_id ? String(app.status_id) : null);

        // Если есть страна, загружаем города для неё
        if (app.country_id) {
          fetchCities(app.country_id);
        }

        // Загружаем контакты с нормализацией полей
        if (app.contacts && app.contacts.length > 0) {
          const normalizedContacts = app.contacts.map(contact => ({
            tool_id: contact.tool_id || null,
            value: contact.value || '',
            is_primary: contact.is_primary || false,
            is_verified: contact.is_verified || false
          }));
          setContacts(normalizedContacts);
        }

        // Загружаем ссылки с нормализацией полей
        if (app.links && app.links.length > 0) {
          const normalizedLinks = app.links.map(link => ({
            id: link.id,
            tool_id: link.tool_id || null,
            tool_name: link.tool_name || null,
            value: link.value || ''
          }));
          setLinks(normalizedLinks);
        }

        // Загружаем коммуникации
        if (app.communications) {
          setCommunications(app.communications);
        }

        // Загружаем важные даты
        if (app.importantDates) {
          setImportantDates(app.importantDates);
        }

        // Загружаем заметки
        if (app.notes) {
          setNotes(app.notes);
        }

        // Загружаем профессии
        if (app.professions) {
          const professionIds = app.professions.map(prof => prof.profession_id.toString());
          setSelectedProfessions(professionIds);
        }
      } else {
        console.error('Failed to fetch application:', response);
        // Не устанавливаем ошибку, чтобы форма могла работать
      }
    } catch (err) {
      console.error('Error fetching application:', err);
      // Не устанавливаем ошибку, чтобы форма могла работать
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Обычное обновление для input полей
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };



  // Обработка успешного изменения статуса
  const handleTransitionSuccess = async (newJobApplicationData) => {
    try {
      console.log('🔍 Status transition successful, updating job application data');

      // Закрываем модал
      setShowTransitionModal(false);

      // Обновляем данные с новым статусом
      setOriginalStatusId(String(transitionData.toStatus.id));
      setFormData(prev => ({
        ...prev,
        status_id: String(transitionData.toStatus.id)
      }));

      // Теперь обновляем остальные данные (без статуса, так как он уже обновлен)
      if (pendingSubmitData) {
    
        await jobApplications.updateJobApplication(id, pendingSubmitData);

        toast.success(`Status changed to "${transitionData.toStatus.name}" and data updated`);
        // Очищаем состояние
        setPendingSubmitData(null);
        setTransitionData(null);
        // Перенаправляем на список
        navigate('/job-applications');
      } else {
        // Если нет других данных для обновления, просто показываем успех
        toast.success(`Status changed to "${transitionData.toStatus.name}"`);
        // Очищаем состояние
        setPendingSubmitData(null);
        setTransitionData(null);
        navigate('/job-applications');
      }
    } catch (err) {
      console.error('Error updating job application after status change:', err);

      // Показываем ошибку пользователю
      if (err.response?.data?.message) {
        toast.error(`Status changed to "${transitionData.toStatus.name}", but data update failed: ${err.response.data.message}`);
      } else {
        toast.error(`Status changed to "${transitionData.toStatus.name}", but data update failed. Please fix the errors and try again.`);
      }

      // Очищаем состояние модала
      setPendingSubmitData(null);
      setTransitionData(null);

      // НЕ делаем navigate - оставляем пользователя на форме для исправления ошибок
    }
  };

  // Обработка ошибки изменения статуса
  const handleTransitionError = (err) => {
    console.error('Error changing status:', err);
    toast.error(formatApiError(err, 'Error changing status'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    // Валидация обязательных полей
    const validationErrors = [];
    let firstInvalidSection = null;

    if (!formData.name.trim()) {
      validationErrors.push('Name is required');
      firstInvalidSection = firstInvalidSection || 'section-basic';
    }

    if (!formData.manager_id) {
      validationErrors.push('Manager is required');
      firstInvalidSection = firstInvalidSection || 'section-location';
    }

    if (!formData.status_id) {
      validationErrors.push('Status is required');
      firstInvalidSection = firstInvalidSection || 'section-location';
    }

    if (selectedProfessions.length === 0) {
      validationErrors.push('At least one profession is required');
      firstInvalidSection = firstInvalidSection || 'section-professions';
    }

    const validContacts = contacts.filter(contact => contact.tool_id && contact.value);
    if (validContacts.length === 0) {
      validationErrors.push('At least one contact is required');
      firstInvalidSection = firstInvalidSection || 'section-contacts';
    }

    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      if (firstInvalidSection) {
        const el = document.getElementById(firstInvalidSection);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setDuplicateContacts(null);

      // Prepare data for submission
      const validImportantDates = importantDates.filter(date => date.date_type && date.date_value);
      const validNotes = notes.filter(note => note.note_type && note.note && note.note.trim().length > 0);

      const submitData = {
        ...formData,
        gender: formData.gender || null, // Отправляем null вместо пустой строки
        country_id: formData.country_id ? parseInt(formData.country_id) : null,
        city_id: formData.city_id ? parseInt(formData.city_id) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        age: formData.age ? parseInt(formData.age) : null,
        birthday: formData.birthday || null,
        job_post_id: formData.job_post_id ? parseInt(formData.job_post_id) : null,
        contacts: validContacts,
        links: links
          .filter(link => link.tool_id && link.value)
          .map(link => ({
            ...(typeof link.id === 'number' ? { id: link.id } : {}),
            tool_id: link.tool_id,
            tool_name: link.tool_name || null,
            value: link.value
          })),
        communications: communications
          .filter(communication => communication.manager_id && communication.notes)
          .map(communication => {
            // Удаляем временный ID (строки начинающиеся с "temp-")
            if (typeof communication.id === 'string' && communication.id.startsWith('temp-')) {
              const { id, ...rest } = communication;
              return rest;
            }
            return communication;
          }),
        important_dates: validImportantDates.map(date => ({
          date_type: date.date_type,
          date_value: date.date_value,
          description: date.description || null
        })),
        notes: validNotes.map(note => ({
          id: note.id && typeof note.id === 'number' && note.id > 1000000000000 ? undefined : note.id, // Исключаем временные ID
          note_type: note.note_type,
          note: note.note
        })),
        professions: selectedProfessions.map(professionId => {
          const profession = professionsOptions.find(p => p.id.toString() === professionId);
          return {
            profession_id: parseInt(professionId),
            profession_name: profession?.name || '',
            department_id: profession?.department_id || null,
            department_name: profession?.department?.name || profession?.department_name || ''
          };
        })
      };

      // Проверяем, изменился ли статус при редактировании
      if (isEditing && originalStatusId && originalStatusId !== formData.status_id) {
        console.log('🔍 Status changed, showing transition modal');

        // Находим объекты статусов
        const fromStatus = statusOptions.find(s => String(s.id) === originalStatusId);
        const toStatus = statusOptions.find(s => String(s.id) === formData.status_id);

        if (!fromStatus || !toStatus) {
          toast.error('Status information not found');
          setLoading(false);
          return;
        }

        // Подготавливаем данные для модала
        setTransitionData({
          jobApplicationId: id,
          jobApplicationName: formData.name,
          fromStatus: { id: fromStatus.id, name: fromStatus.name, color: fromStatus.color },
          toStatus: { id: toStatus.id, name: toStatus.name, color: toStatus.color }
        });

        // Сохраняем данные для последующей отправки
        setPendingSubmitData(submitData);

        // Показываем модал
        setShowTransitionModal(true);
        setLoading(false);
        return;
      }

      // Если статус не изменился или создаём новый, продолжаем обычную отправку
      if (isEditing) {
        await jobApplications.updateJobApplication(id, submitData);
      } else {
        await jobApplications.createJobApplication(submitData);
        clearStorage();
      }

      navigate('/job-applications');
    } catch (err) {
      console.error('Error saving application:', err);
      console.error('Error response:', err.response?.data);

      // Обработка различных типов ошибок с бекенда
      if (err.response?.data) {
        const errorData = err.response.data;

        // 409 Conflict - дублирующиеся контакты
        if (err.response.status === 409 && errorData.duplicates) {
          setDuplicateContacts(errorData.duplicates);
          setError(errorData.message || 'Duplicate contacts found');
        }
        // Массив ошибок валидации с полями
        else if (errorData.errors && Array.isArray(errorData.errors)) {
          // Проверяем, есть ли у ошибок поля field
          const hasFields = errorData.errors.some(e => e.field);

          if (hasFields) {
            // Форматируем ошибки с указанием полей
            const errorMessages = errorData.errors.map(e => {
              const field = e.field || 'Unknown field';
              const message = e.message || e.msg || e;
              const value = e.value !== undefined ? ` (value: ${e.value})` : '';
              return `${field}: ${message}${value}`;
            }).join('\n');
            setError(`${errorData.message || 'Validation error'}:\n\n${errorMessages}`);
          } else {
            // Простой список сообщений
            const errorMessages = errorData.errors.map(e => e.message || e.msg || e).join(', ');
            setError(`${errorData.message || 'Validation error'}: ${errorMessages}`);
          }
        }
        // Joi validation errors с details
        else if (errorData.details && Array.isArray(errorData.details)) {
          const errorMessages = errorData.details.join(', ');
          setError(`${errorData.message || 'Validation error'}: ${errorMessages}`);
        }
        // Одиночная ошибка валидации
        else if (errorData.error) {
          const errorMessage = typeof errorData.error === 'string' ? errorData.error : errorData.error.message;
          const details = errorData.details ? `\n\nDetails: ${errorData.details}` : '';
          setError(errorData.message ? `${errorData.message}: ${errorMessage}${details}` : `${errorMessage}${details}`);
        }
        // Обычное сообщение об ошибке
        else if (errorData.message) {
          setError(errorData.message);
        }
        // Fallback
        else {
          setError('Failed to save application. Please check your input and try again.');
        }
      }
      // Ошибка сети или другая
      else if (err.message) {
        setError(`Network error: ${err.message}`);
      }
      else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Show confirmation dialog only when creating (not when editing)
    if (!isEditing) {
      setShowCancelConfirm(true);
    } else {
      navigate('/job-applications');
    }
  };

  const handleConfirmCancel = () => {
    clearStorage();
    setShowCancelConfirm(false);
    navigate('/job-applications');
  };

  const handleKeepEditing = () => {
    setShowCancelConfirm(false);
  };

  // Handle AI Resume Parser data
  // Note: Data is automatically saved to localStorage via the auto-save useEffect
  const handleResumeDataFill = (aiData) => {
    try {
      const mappedData = mapResumeDataToForm(aiData, {
        countryOptions,
        cityOptions,
        allCities,
        professionsOptions,
        toolsOptions
      });

      // Сохраняем raw AI data и unmapped items для ParsedDataPanel
      setAiRawData(aiData);
      setUnmappedData(mappedData.unmapped);

      // Fill form data
      // mapResumeDataToForm resolves country_id/city_id via findCountryId/findCityId,
      // which return the raw (numeric) option id — stringify to match the
      // Autocomplete value contract used by the Country/City selects below.
      setFormData(prev => ({
        ...prev,
        ...mappedData.formData,
        ...(mappedData.formData.country_id != null ? { country_id: String(mappedData.formData.country_id) } : {}),
        ...(mappedData.formData.city_id != null ? { city_id: String(mappedData.formData.city_id) } : {}),
      }));

      // Fill contacts
      if (mappedData.contacts && mappedData.contacts.length > 0) {
        setContacts(mappedData.contacts);
      }

      // Fill professions
      if (mappedData.professions && mappedData.professions.length > 0) {
        setSelectedProfessions(mappedData.professions);
      }

      // Load cities for selected country (if city not already found in allCities)
      if (mappedData.formData.country_id && !mappedData.formData.city_id) {
        fetchCities(mappedData.formData.country_id);
      }

      // Auto-save will be triggered automatically by the useEffect

      // Auto-open parsed data panel after filling
      setIsParsedDataPanelOpen(true);
    } catch (error) {
      console.error('Error filling form with resume data:', error);
    }
  };

  // Re-map: повторний маппинг aiRawData з поточними options
  const handleRemap = () => {
    if (!aiRawData) return;

    const mappedData = mapResumeDataToForm(aiRawData, {
      countryOptions,
      cityOptions,
      allCities,
      professionsOptions,
      toolsOptions
    });

    setUnmappedData(mappedData.unmapped);

    // Fill form data
    setFormData(prev => ({
      ...prev,
      ...mappedData.formData,
      ...(mappedData.formData.country_id != null ? { country_id: String(mappedData.formData.country_id) } : {}),
      ...(mappedData.formData.city_id != null ? { city_id: String(mappedData.formData.city_id) } : {}),
    }));

    if (mappedData.contacts && mappedData.contacts.length > 0) {
      setContacts(mappedData.contacts);
    }
    if (mappedData.professions && mappedData.professions.length > 0) {
      setSelectedProfessions(mappedData.professions);
    }

    const newUnmappedCount = (mappedData.unmapped.professions?.length || 0) +
      (mappedData.unmapped.contacts?.length || 0) +
      (mappedData.unmapped.country ? 1 : 0) +
      (mappedData.unmapped.city ? 1 : 0);

    if (newUnmappedCount === 0) {
      toast.success('All items mapped successfully!');
    } else {
      toast.success('Re-mapped! Some items still unmapped.');
    }
  };

  // Показываем Loader во время начальной загрузки данных
  if (initialLoading) {
    return (
      <Loader
        variant="component"
        text={isEditing ? "Loading application data..." : "Loading form data..."}
        size="md"
      />
    );
  }

  if (loading) {
    return (
      <Loader
        variant="component"
        text="Saving application..."
        size="md"
      />
    );
  }

  return (
    <div className="bg-light-bg-primary dark:bg-dark-bg-primary">
      <title>{id ? 'Edit Application | Talent Service' : 'New Application | Talent Service'}</title>
      <div>
        <div className="mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex min-w-0 items-center gap-2 sm:gap-4">
              <Link
                to="/job-applications"
                className="flex items-center gap-2 px-2 sm:px-3 py-2 text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back</span>
              </Link>
              <h1 className="min-w-0 text-xl font-bold text-light-text-primary dark:text-dark-text-primary sm:text-2xl">
                {isEditing ? 'Edit Application' : 'Create New Application'}
              </h1>
            </div>
            {!isEditing && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setIsResumeParserOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors text-sm sm:text-base"
                >
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">AI Parser</span>
                  <span className="sm:hidden">📄</span>
                </button>
                {aiRawData && (
                  <button
                    onClick={() => setIsParsedDataPanelOpen(true)}
                    className="flex items-center gap-2 px-3 py-2 border border-light-border-primary dark:border-dark-border-primary text-light-text-primary dark:text-dark-text-primary bg-light-bg-secondary dark:bg-dark-bg-tertiary rounded-md hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-secondary focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors text-sm sm:text-base"
                  >
                    <Info className="w-4 h-4" />
                    <span className="hidden sm:inline">AI Data</span>
                    {unmappedData && (unmappedData.professions?.length > 0 || unmappedData.contacts?.length > 0 || unmappedData.country || unmappedData.city) && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                        {(unmappedData.professions?.length || 0) + (unmappedData.contacts?.length || 0) + (unmappedData.country ? 1 : 0) + (unmappedData.city ? 1 : 0)}
                      </span>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-500 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {duplicateContacts && duplicateContacts.length > 0 && (
          <div className="bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-400 dark:border-yellow-500 px-4 py-3 rounded mb-6">
            <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
              Дублирующиеся контакты обнаружены:
            </h3>
            <div className="space-y-3">
              {duplicateContacts.map((dup, index) => {
                const tool = toolsOptions.find(t => t.id === dup.contact.tool_id);
                return (
                  <div key={index} className="bg-white dark:bg-dark-bg-primary p-3 rounded border border-yellow-300 dark:border-yellow-600">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <span className="font-medium">{tool?.name || 'Unknown tool'}:</span> {dup.contact.value}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          Уже используется в заявке: <span className="font-medium">{dup.existingJobApplication.name}</span>
                        </p>
                      </div>
                      <Link
                        to={`/job-applications/${dup.existingJobApplication.id}`}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline whitespace-nowrap"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Перейти к заявке →
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* First row: Basic Information and Contacts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div id="section-basic" className="rounded-lg border border-light-border-primary bg-light-bg-primary p-4 shadow dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:p-6">
              <h2 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-4">Basic Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <TextField
                    label="Name"
                    required
                    fullWidth
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter full name"
                  />
                </div>

                <div>
                  <Autocomplete
                    clearable
                    fullWidth
                    label="Gender"
                    value={formData.gender}
                    onChange={(value) => setFormData(prev => ({ ...prev, gender: value || '' }))}
                    placeholder="Select gender"
                    options={[
                      { value: 'male', label: 'Male' },
                      { value: 'female', label: 'Female' },
                      { value: 'other', label: 'Other' }
                    ]}
                  />
                </div>

                <div>
                  <DatePicker
                    label="Birthday"
                    fullWidth
                    value={formData.birthday ? new Date(formData.birthday) : null}
                    onChange={(d) => setFormData(prev => ({ ...prev, birthday: d ? d.toISOString().slice(0, 10) : '' }))}
                  />
                </div>

                <div>
                  <TextField
                    label="Age"
                    fullWidth
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    min="0"
                    max="120"
                    placeholder="Enter age"
                  />
                </div>
              </div>
            </div>

            <div id="section-contacts" className="rounded-lg border border-light-border-primary bg-light-bg-primary p-4 shadow dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:p-6">
              <ContactForm
                contacts={contacts}
                toolsOptions={toolsOptions}
                onContactsChange={setContacts}
                title="Contacts"
                showTitle={true}
                excludeJobApplicationId={id || null}
                entityType="jobApplication"
              />
            </div>
          </div>

          {/* Second row: Location & Management and Professions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div id="section-location" className="rounded-lg border border-light-border-primary bg-light-bg-primary p-4 shadow dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:p-6">
              <h2 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-4">Location & Management</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Autocomplete
                    clearable
                    fullWidth
                    label="Country"
                    value={formData.country_id}
                    onChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        country_id: value || '',
                        city_id: '' // Сбрасываем выбранный город при смене страны
                      }));
                      fetchCities(value);
                    }}
                    placeholder="Select a country"
                    options={countryOptions.map((country) => ({
                      value: String(country.id),
                      label: country.name
                    }))}
                  />
                </div>

                <div>
                  <Autocomplete
                    clearable
                    fullWidth
                    label="City"
                    value={formData.city_id}
                    onChange={(value) => {
                      if (value) {
                        const selectedCity = allCities.find(city => city.id == value);
                        if (selectedCity && selectedCity.country_id) {
                          setFormData(prev => ({
                            ...prev,
                            city_id: value,
                            country_id: String(selectedCity.country_id)
                          }));
                          // Загружаем города для определенной страны
                          fetchCities(selectedCity.country_id);
                        } else {
                          // Если город выбран, но страна не найдена, показываем все города
                          setFormData(prev => ({ ...prev, city_id: value }));
                          setCityOptions(allCities);
                          setShowAllCities(true);
                        }
                      } else {
                        setFormData(prev => ({ ...prev, city_id: value || '' }));
                      }
                    }}
                    placeholder="Select a city"
                    options={cityOptions.map((city) => ({
                      value: String(city.id),
                      label: city.name
                    }))}
                    disabled={loadingCities}
                  />
                  {loadingCities && (
                    <div className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary mt-1">Loading cities...</div>
                  )}
                  {!loadingCities && showAllCities && (
                    <div className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary mt-1">All cities (select country to filter)</div>
                  )}
                  {!loadingCities && !showAllCities && (
                    <div className="text-sm text-blue-600 dark:text-blue-400 mt-1">Cities filtered by selected country</div>
                  )}
                </div>

                <div>
                  <Autocomplete
                    clearable
                    fullWidth
                    label="Manager *"
                    value={formData.manager_id}
                    onChange={(value) => setFormData(prev => ({ ...prev, manager_id: value || '' }))}
                    placeholder="Select a manager"
                    options={userOptions.map((user) => ({
                      value: String(user.id),
                      label: user.name
                    }))}
                    required
                  />
                </div>

                <div>
                  <Autocomplete
                    clearable
                    fullWidth
                    label="Status *"
                    value={formData.status_id}
                    onChange={(value) => setFormData(prev => ({ ...prev, status_id: value || '' }))}
                    placeholder="Select status"
                    options={
                      // Сортируем только вывод: сам statusOptions трогать нельзя —
                      // из него берётся статус по умолчанию для новой заявки
                      statusOptions
                        .slice()
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                        .map((status) => ({
                          value: String(status.id),
                          label: status.name
                        }))
                    }
                    required
                  />
                </div>

                <div>
                  <Autocomplete
                    clearable
                    fullWidth
                    label="Job Post"
                    value={formData.job_post_id}
                    onChange={(value) => setFormData(prev => ({ ...prev, job_post_id: value || '' }))}
                    placeholder="Search and select job post (optional)"
                    options={(() => {
                      const opts = jobPostOptions.map((post) => ({
                        value: String(post.id),
                        label: post.name || `Job Post #${post.id}`
                      }));
                      // Если job_post_id задан, но поста нет в опциях — заглушка для удалённого поста
                      if (formData.job_post_id && !opts.some(o => o.value === formData.job_post_id)) {
                        opts.unshift({ value: formData.job_post_id, label: '[Deleted Post]' });
                      }
                      return opts;
                    })()}
                  />
                </div>
              </div>
            </div>

            <div id="section-professions" className="rounded-lg border border-light-border-primary bg-light-bg-primary p-4 shadow dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:p-6">
              <h2 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-4">Professions *</h2>

              <Autocomplete
                multiple
                clearable
                fullWidth
                label="Select Professions"
                value={selectedProfessions}
                onChange={(value) => setSelectedProfessions(value || [])}
                placeholder="Search and select professions..."
                options={professionsOptions.map((profession) => ({
                  value: profession.id.toString(),
                  label: profession.name
                }))}
                required
              />

              {professionsOptions.length === 0 && (
                <p className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm mt-2">No professions available</p>
              )}
              
              {submitAttempted && selectedProfessions.length === 0 && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-2">At least one profession is required</p>
              )}
            </div>
          </div>

          {/* Links */}
          <div id="section-links" className="rounded-lg border border-light-border-primary bg-light-bg-primary p-4 shadow dark:border-dark-border-primary dark:bg-dark-bg-secondary sm:p-6">
            <LinkForm
              links={links}
              linkToolsOptions={linkToolsOptions}
              onLinksChange={setLinks}
              title="Links"
              showTitle={true}
            />
          </div>

          {/* Communications */}
          <CommunicationsManager
            entityId={id}
            entityType="JobApplication"
            communications={communications}
            onCommunicationsChange={setCommunications}
            apiMethods={id ? {
              getCommunications: jobApplications.getJobApplicationCommunications,
              addCommunication: jobApplications.addJobApplicationCommunication,
              updateCommunication: jobApplications.updateJobApplicationCommunication,
              deleteCommunication: jobApplications.deleteJobApplicationCommunication
            } : null}
            showTitle={true}
            title="Communications"
            className="mt-6"
          />

          {/* Important Dates */}
          <ImportantDatesManager
            entityId={id}
            entityType="JobApplication"
            importantDates={importantDates}
            onImportantDatesChange={setImportantDates}
            showTitle={true}
            title="Important Dates"
            className="mt-6"
          />

          {/* Notes */}
          <NotesManager
            entityId={id}
            entityType="JobApplication"
            notes={notes}
            onNotesChange={setNotes}
            showTitle={true}
            title="Notes"
            className="mt-6"
          />

          <FormActions className="sticky bottom-0 z-10 -mx-4 border-t border-light-border-primary bg-light-bg-primary px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-2px_8px_rgba(0,0,0,0.06)] dark:border-dark-border-primary dark:bg-dark-bg-primary sm:mx-0 sm:rounded-lg">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-light-border-primary dark:border-dark-border-primary rounded-md text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : (isEditing ? 'Update Application' : 'Create Application')}
            </button>
          </FormActions>
        </form>
      </div>

      {/* Resume Parser Drawer */}
      <ResumeParserDrawer
        isOpen={isResumeParserOpen}
        onClose={() => setIsResumeParserOpen(false)}
        onParsed={handleResumeDataFill}
      />

      {/* Parsed Data Panel */}
      <ParsedDataPanel
        isOpen={isParsedDataPanelOpen}
        onClose={() => setIsParsedDataPanelOpen(false)}
        aiRawData={aiRawData}
        unmapped={unmappedData}
        onToolCreated={null}
        allowCreateTool={false}
        onRemap={handleRemap}
      />

      {/* Confirm Dialog for Cancel */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={handleKeepEditing}
        onConfirm={handleConfirmCancel}
        title="Leave without saving?"
        message="If you leave, any unsaved data will be lost."
        confirmText="Leave"
        cancelText="Stay"
      />

      {/* Status Transition Modal */}
      <StatusTransitionModal
        isOpen={showTransitionModal}
        onClose={() => {
          setShowTransitionModal(false);
          setTransitionData(null);
          setPendingSubmitData(null);
        }}
        entityType="job_application"
        entityId={transitionData?.jobApplicationId}
        entityName={transitionData?.jobApplicationName}
        fromStatus={transitionData?.fromStatus}
        toStatus={transitionData?.toStatus}
        onSuccess={handleTransitionSuccess}
        onError={handleTransitionError}
      />
    </div>
  );
};

export default JobApplicationForm;
